// BRIEF 86 PHASE 2, STEP 7 (run by brief 90 §5) — the Case Team section, once.
//
// The brief's largest pair and its last section: 610 lines in `CaseView` and 526
// in `CollectiveCaseView`, 63% normalised. Three commits of harmonisation took
// it to 95% — and the distance was not style. Three of the eight divergences
// were defects, all on the collective side, all invisible because the correct
// version was in the other file:
//
//   the Hrs Wk column filtered time entries by `e.caId === cs.id` inside the
//   COLLECTIVE view, where `cs.id` indexes a different sequence — a column
//   showing another matter's hours, or none;
//   "open" was spelled inline as three terminal codes instead of the tenant's
//   own `OPEN_ST` ladder, so a firm's seventh status counted in one view only;
//   and removing a collaborator wrote straight through — no confirm, no undo,
//   and no refusal for a responsible partner, all three of which CaseView had.
//
// What is left, and it is the whole parameter list:
//
//   THE CONTAINER DISCRIMINATOR — `kind`, which reaches exactly two places:
//   `matterIdOf` (a task's `caseId` / a time entry's `caId` vs their `ccId`)
//   and `TeamAccessPanel`'s `scope`.
//
//   THE HOST'S WRITES — `onCollaboratorsChange` and `onCollaboratorsRestore`.
//   TWO handles, not one, and the difference is the point: the first writes
//   from THIS render's array (add, remove, ratio); the second is the undo path
//   and must read the LATEST array — `caRef.current` / `ccRef.current` — because
//   an undo fires after a toast, from a closure minutes old. Folding them into
//   one seam would have silently made undo restore a stale roster.
//
//   THE HOST'S RATE TABLE — `finCalc`, each view's own effective-rate map.
//
// Everything else in this file is CaseView's, line for line.
import type { MouseEvent as ReactMouseEvent } from "react";
import { X } from "lucide-react";
import TeamAccessPanel from "./TeamAccessPanel";
import { SectionHeader, AddButton, LockButton } from "./SharedUI";
import Pager from "./Pager";
import { pageOf } from "../hooks/usePager";
import { choosableCo, isArchivedCo } from "../lib/collaboratorState";
import { roleLabel, roleDefaultRatio } from "../hooks/useRoleLabels";
import { pname, fq, fmin, I } from "../utils";
import { RAD, FS, Z } from "../lib/tokens";
import type { TFn } from "../lib/taxonomyTypes";
import Table from "./Table";

// The read-sets this section uses. Partial by design, and typed rather than
// `any` because the ratchet allows a new file none.
type Person = {
  id: number;
  status?: string | null;
  role?: string | null;
  coType?: string | null;
  defaultRatio?: number | null;
  roleDefaultRatio?: number | null;
  [k: string]: unknown;
};
type Collab = { coId: number; ratio?: number };
type Task = {
  id: number;
  caseId?: number | null;
  ccId?: number | null;
  status?: string;
  dueDate?: string | null;
  assignedCoId?: number | null;
  [k: string]: unknown;
};
type Entry = {
  coId?: number | null;
  caId?: number | null;
  ccId?: number | null;
  date?: string | null;
  min?: number | null;
  [k: string]: unknown;
};

type Props = {
  // 'case' | 'cc' — reaches `matterIdOf` and TeamAccessPanel's scope, nothing else.
  kind: "case" | "cc";
  cs: {
    id: number;
    caseCollaborators?: Collab[];
    responsiblePartners?: number[];
    referenceHourlyRate?: number | null;
    [k: string]: unknown;
  };
  co: Person[];
  tk: Task[];
  te: Entry[];
  // The tenant's own open-status ladder (App: openStatusCodes(taskStatusEntries)).
  OPEN_ST: string[];
  canTerms?: boolean;
  canAssignCollabs?: boolean;
  isPartnerStrict?: boolean;
  _isRPHere?: boolean;
  _permViewer?: { isAdmin?: boolean } | null;
  // The host's effective-rate map, keyed by matter id then collaborator id.
  finCalc?: Record<
    number,
    { collabRates?: Record<number, number | null> } | undefined
  > | null;
  teamOpen: boolean;
  setTeamOpen: (fn: (o: boolean) => boolean) => void;
  teamViewLocked: boolean;
  setTeamViewLocked: (fn: (v: boolean) => boolean) => void;
  collabLocked: boolean;
  setCollabLocked: (fn: (v: boolean) => boolean) => void;
  addCoOpen: boolean;
  setAddCoOpen: (v: boolean | ((v: boolean) => boolean)) => void;
  crPage: number;
  setCrPage: (v: number) => void;
  // Writes from THIS render's roster: add, remove, ratio.
  onCollaboratorsChange: (next: Collab[]) => void;
  // The undo path. Takes the LATEST roster and returns the restored one — the
  // host resolves `current` from its ref, never from a captured render.
  onCollaboratorsRestore: (fn: (cur: Collab[]) => Collab[]) => void;
  confirmAndUndo: (o: {
    label?: unknown;
    toastName?: unknown;
    onConfirm: () => void;
    undo?: {
      snapshot: { entry: Collab; index: number };
      restore: (snap: { entry: Collab; index: number }) => void;
    } | null;
  }) => void;
  t: TFn;
};

export default function CaseTeamSection({
  kind,
  cs,
  co,
  tk,
  te,
  OPEN_ST,
  canTerms,
  canAssignCollabs,
  isPartnerStrict,
  _isRPHere,
  _permViewer,
  finCalc,
  teamOpen,
  setTeamOpen,
  teamViewLocked,
  setTeamViewLocked,
  collabLocked,
  setCollabLocked,
  addCoOpen,
  setAddCoOpen,
  crPage,
  setCrPage,
  onCollaboratorsChange,
  onCollaboratorsRestore,
  confirmAndUndo,
  t,
}: Props) {
  // The container discriminator, resolved once. A task carries `caseId` on a
  // case and `ccId` on a collective case; a time entry carries `caId` / `ccId`.
  const matterIdOf = (row: Task | Entry) =>
    kind === "cc" ? row.ccId : ((row as Task).caseId ?? (row as Entry).caId);
  return (
    <div
      className="csSec-team"
      style={{ gridArea: "collabRates", minWidth: 0 }}
    >
      {/* Phase 28 (E) — consolidated "Case Team" section: assigned
          collaborators & rates + the Viewing / Editing rights panes. */}
      <SectionHeader
        title={t("caseTeam.title", { fallback: "Case Team" })}
        collapsible
        open={teamOpen}
        onToggle={() => setTeamOpen((o) => !o)}
      />
      {teamOpen && (
        <>
    {/* R1 — Assigned Collaborators pane: full-width .dc card, title is a
        card title (not a nested collapsible), matching the rights panes. */}
    <div
      className="csSec-collabRates"
      style={{
        minWidth: 0,
        marginBottom: 10,
        padding: 12,
        borderRadius: RAD.r8,
        border: "1px solid var(--br)",
        background: "var(--bi)",
        display: isPartnerStrict ? undefined : "none",
      }}
    >
      {isPartnerStrict &&
        (() => {
          const canSeeRates = canTerms;
          /* Phase M+2b: rate display gates on caseView.terms (was canFin). Rate column visibility and the SectionHeader's "X EUR/h Ex. Costs Ex. VAT" subtitle align on the same gate. */ const nw =
            new Date();
          const dw = nw.getDay();
          const mn = new Date(nw);
          mn.setDate(nw.getDate() - (dw === 0 ? 6 : dw - 1));
          const wkS = mn.toISOString().slice(0, 10);
          const sn = new Date(mn);
          sn.setDate(mn.getDate() + 6);
          const wkE = sn.toISOString().slice(0, 10);
          return (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  marginBottom: 8,
                }}
              >
                <h4
                  style={{
                    fontSize: FS.s11,
                    fontWeight: 700,
                    color: "var(--ts)",
                    textTransform: "uppercase",
                    letterSpacing: ".4px",
                    margin: 0,
                  }}
                >
                  {t("case.collab.section", {
                    fallback: "Assigned Collaborators",
                  })}
                </h4>
                {canSeeRates && cs.referenceHourlyRate != null && (
                  <span style={{ fontSize: FS.s10, color: "var(--tm)" }}>
                    {fq(cs.referenceHourlyRate)}{" "}
                    {t("case.collab.rate_subtitle", {
                      fallback: "EUR/h Ex. Costs Ex. VAT",
                    })}
                  </span>
                )}
                <div
                  style={{
                    marginLeft: "auto",
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                  }}
                >
                    {(!canSeeRates || !collabLocked) &&
                      canAssignCollabs && (
                        <div style={{ position: "relative" }}>
                          <AddButton
                            /* 188 §F.3 [glance correction] — the standard label
                               (186 (7)); `case.collab.add` ("+ Ajouter un
                               collaborateur assigné") stays seeded, unread. */
                            label={t("common.add_button.add", {
                              fallback: "Add",
                            })}
                            onClick={(e: ReactMouseEvent) => {
                              e.stopPropagation();
                              setAddCoOpen((v) => !v);
                            }}
                          />
                          <div
                            className="addCoDD"
                            style={{
                              display: addCoOpen ? "block" : "none",
                              position: "absolute",
                              top: "100%",
                              right: 0,
                              marginTop: 4,
                              background: "var(--bc)",
                              border: "1px solid var(--br)",
                              borderRadius: RAD.r8,
                              boxShadow: "0 6px 20px rgba(0,0,0,.6)",
                              zIndex: Z.inlineMenu,
                              width: 220,
                              maxHeight: 200,
                              overflow: "hidden",
                            }}
                          >
                            <input
                              className="field-input"
                              style={{
                                margin: 0,
                                padding: "5px 8px",
                                fontSize: FS.s10,
                                border: "none",
                                borderBottom: "1px solid var(--br)",
                                borderRadius: RAD.r0,
                                width: "100%",
                                boxSizing: "border-box",
                              }}
                              placeholder={t("common.search_placeholder", {
                                fallback: "Search…",
                              })}
                              onChange={(e) => {
                                const q2 = e.target.value.toLowerCase();
                                e.currentTarget.parentElement!
                                  .querySelectorAll("[data-coid]")
                                  .forEach((node: Element) => {
                                    const el2 = node as HTMLElement;
                                    el2.style.display = (el2.dataset.name || "")
                                      .toLowerCase()
                                      .includes(q2)
                                      ? "flex"
                                      : "none";
                                  });
                              }}
                            />
                            <div
                              style={{ maxHeight: 160, overflowY: "auto" }}
                            >
                              {(() => {
                                const avail = choosableCo(co).filter(
                                  (c: Person) =>
                                    !(cs.caseCollaborators || []).some(
                                      (x: Collab) => x.coId === c.id,
                                    ),
                                );
                                const int2 = avail.filter(
                                  (c: Person) =>
                                    !c.coType || c.coType === "internal",
                                );
                                const ext2 = avail.filter(
                                  (c: Person) => c.coType === "external",
                                );
                                const addCo = (c: Person) => {
                                  const ratio =
                                    c.defaultRatio ??
                                    c.roleDefaultRatio ??
                                    roleDefaultRatio(c.role || "") ??
                                    100;
                                  const ncc = [
                                    ...(cs.caseCollaborators || []),
                                    { coId: c.id, ratio },
                                  ];
                                  onCollaboratorsChange(ncc);
                                  setAddCoOpen(false);
                                };
                                const Item = (c: Person) => (
                                  <div
                                    key={c.id}
                                    data-coid={c.id}
                                    data-name={pname(c)}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 6,
                                      padding: "5px 8px",
                                      cursor: "pointer",
                                      fontSize: FS.s10,
                                    }}
                                    onMouseEnter={(e2) =>
                                      (e2.currentTarget.style.background =
                                        "var(--bh)")
                                    }
                                    onMouseLeave={(e2) =>
                                      (e2.currentTarget.style.background =
                                        "none")
                                    }
                                    onClick={() => addCo(c)}
                                  >
                                    <span style={{ flex: 1 }}>
                                      {pname(c)}
                                    </span>
                                    <span
                                      style={{
                                        fontSize: FS.s9,
                                        color: "var(--tm)",
                                      }}
                                    >
                                      {roleLabel(c.role || "")}
                                    </span>
                                  </div>
                                );
                                return (
                                  <>
                                    {int2.map(Item)}
                                    {ext2.length > 0 && int2.length > 0 && (
                                      <div
                                        style={{
                                          fontSize: FS.s8,
                                          color: "var(--tm)",
                                          padding: "3px 8px",
                                          borderTop: "1px solid var(--br)",
                                        }}
                                      >
                                        {t("case.collab.external_group", {
                                          fallback: "External",
                                        })}
                                      </div>
                                    )}
                                    {ext2.map(Item)}
                                    {avail.length === 0 && (
                                      <div
                                        style={{
                                          padding: "10px 8px",
                                          fontSize: FS.s10,
                                          color: "var(--tm)",
                                          textAlign: "center",
                                        }}
                                      >
                                        {t("case.collab.all_assigned", {
                                          fallback:
                                            "All collaborators assigned",
                                        })}
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      )}
                    {canSeeRates && canAssignCollabs && (
                      <LockButton
                        locked={collabLocked}
                        onToggle={() => setCollabLocked((v) => !v)}
                      />
                    )}
                </div>
              </div>
              <div className="table-wrap" style={{ marginBottom: 6 }}>
                <Table>
                  <thead>
                    <tr>
                      <th>{t("case.collab.col.name")}</th>
                      <th>{t("case.collab.col.role")}</th>
                      <th title={t("case.collab.tip.open_tasks")}>
                        {t("case.collab.col.open")}
                      </th>
                      <th title={t("case.collab.tip.tasks_due_week")}>
                        {t("case.collab.col.due_wk")}
                      </th>
                      <th title={t("case.collab.tip.in_progress")}>
                        {t("case.collab.col.in_prog")}
                      </th>
                      <th title={t("case.collab.tip.completed")}>
                        {t("case.collab.col.done")}
                      </th>
                      {canSeeRates && (
                        <>
                          <th title={t("case.collab.tip.hours_this_week")}>
                            {t("case.collab.col.hrs_wk")}
                          </th>
                          <th>{t("case.collab.col.default_pct")}</th>
                          <th>{t("case.collab.col.case_pct")}</th>
                          <th>{t("case.collab.col.eff_rate")}</th>
                        </>
                      )}
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const __all = cs.caseCollaborators || [];
                      /* 188 D.3 — the arithmetic is `pageOf`'s (ten; `Math.max(1,
                         ceil)` · `Math.min(page, tot - 1)` · the slice — this copy's
                         own three expressions, identity replayed over 4,800 cases,
                         0 differences); the page state stays CaseView's `crPage`. */
                      const { cur: __cur, tot: __tot, pageItems: __pg } = pageOf(__all, crPage);
                      return (
                        <>
                          {__pg.map((cc: Collab) => {
                            const c = co.find((x: Person) => x.id === cc.coId);
                            if (!c) return null;
                            const defRatio =
                              c.defaultRatio ??
                              c.roleDefaultRatio ??
                              roleDefaultRatio(c.role || "") ??
                              100;
                            const isRP = (
                              cs.responsiblePartners || []
                            ).includes(cc.coId);
                            const actCell = (
                              <td>
                                {isRP ? (
                                  <span
                                    title={t("roles.rp.full", {
                                      fallback: "Responsible Partner",
                                    })}
                                    style={{
                                      fontSize: FS.s9,
                                      color: "var(--tm)",
                                      padding: "1px 4px",
                                    }}
                                  >
                                    {t("roles.rp.abbr", { fallback: "RP" })}
                                  </span>
                                ) : canAssignCollabs &&
                                  (!canSeeRates || !collabLocked) ? (
                                  <button
                                    className="btn btn-danger"
                                    style={{
                                      padding: "1px 4px",
                                      fontSize: FS.s9,
                                    }}
                                    onClick={() =>
                                      confirmAndUndo({
                                        label: "this collaborator",
                                        toastName: `Removed ${pname(c)}`,
                                        onConfirm: () => {
                                          const ncc = (
                                            cs.caseCollaborators || []
                                          ).filter(
                                            (x: Collab) => x.coId !== cc.coId,
                                          );
                                          onCollaboratorsChange(ncc);
                                        },
                                        undo: {
                                          snapshot: {
                                            entry: cc,
                                            index: (
                                              cs.caseCollaborators || []
                                            ).findIndex(
                                              (x: Collab) => x.coId === cc.coId,
                                            ),
                                          },
                                          restore: (snap) =>
                                            onCollaboratorsRestore((cur) => {
                                              const arr = [...cur];
                                              if (
                                                snap.index >= 0 &&
                                                snap.index <= arr.length
                                              )
                                                arr.splice(
                                                  snap.index,
                                                  0,
                                                  snap.entry,
                                                );
                                              else arr.push(snap.entry);
                                              return arr;
                                            }),
                                        },
                                      })
                                    }
                                  >
                                    <X size={I.s} />
                                  </button>
                                ) : null}
                              </td>
                            );
                            const ccTk = tk.filter(
                              (t: Task) =>
                                matterIdOf(t) === cs.id &&
                                t.assignedCoId === cc.coId,
                            );
                            const openN = ccTk.filter((t: Task) =>
                              OPEN_ST.includes(t.status as string),
                            ).length;
                            const dueWkN = ccTk.filter(
                              (t: Task) =>
                                t.dueDate &&
                                t.dueDate >= wkS &&
                                t.dueDate <= wkE &&
                                OPEN_ST.includes(t.status as string),
                            ).length;
                            const inProgN = ccTk.filter(
                              (t: Task) => t.status === "inprogress",
                            ).length;
                            const doneN = ccTk.filter(
                              (t: Task) => t.status === "completed",
                            ).length;
                            const hrsWk = te
                              .filter(
                                (e: Entry) =>
                                  e.coId === cc.coId &&
                                  matterIdOf(e) === cs.id &&
                                  e.date &&
                                  e.date >= wkS &&
                                  e.date <= wkE,
                              )
                              .reduce(
                                (acc: number, e: Entry) => acc + (e.min || 0) / 60,
                                0,
                              );
                            return (
                              <tr key={cc.coId}>
                                <td>
                                  <span className="name-cell" style={isArchivedCo(c) ? { opacity: 0.45 } : undefined}>{pname(c)}</span>
                                </td>
                                <td>
                                  <span className="tag">
                                    {roleLabel(c.role || "")}
                                  </span>
                                </td>
                                <td className="mono">{openN || "—"}</td>
                                <td className="mono">{dueWkN || "—"}</td>
                                <td className="mono">{inProgN || "—"}</td>
                                <td className="mono">{doneN || "—"}</td>
                                {canSeeRates && (
                                  <>
                                    <td className="mono">
                                      {hrsWk > 0
                                        ? fmin(Math.round(hrsWk * 60))
                                        : "—"}
                                    </td>
                                    <td className="mono">{defRatio}%</td>
                                    <td>
                                      {canAssignCollabs && !collabLocked ? (
                                        <div
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 3,
                                          }}
                                        >
                                          <input
                                            type="number"
                                            className="field-input"
                                            style={{
                                              width: 52,
                                              padding: "2px 5px",
                                              fontSize: FS.s11,
                                              // Phase 25.1 B.4: drop the .fi
                                              // 30px min-height so unlocking
                                              // the % field does not grow the
                                              // row vs the locked text height.
                                              minHeight: 0,
                                              height: 22,
                                              boxSizing: "border-box",
                                            }}
                                            min={0}
                                            max={200}
                                            value={cc.ratio}
                                            onChange={(e) => {
                                              const v = Math.max(
                                                0,
                                                +e.target.value,
                                              );
                                              const ncc2 = (
                                                cs.caseCollaborators || []
                                              ).map((x: Collab) =>
                                                x.coId === cc.coId
                                                  ? { ...x, ratio: v }
                                                  : x,
                                              );
                                              onCollaboratorsChange(ncc2);
                                            }}
                                          />
                                          <span
                                            style={{
                                              fontSize: FS.s10,
                                              color: "var(--tm)",
                                            }}
                                          >
                                            %
                                          </span>
                                        </div>
                                      ) : (
                                        <span
                                          className="mono"
                                          style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            height: 22,
                                          }}
                                        >
                                          {cc.ratio}%
                                        </span>
                                      )}
                                    </td>
                                    <td className="mono">
                                      {fq(finCalc?.[cs.id]?.collabRates?.[cc.coId] ?? null)}{" "}
                                      {t("common.eur_per_hour", {
                                        fallback: "EUR/h",
                                      })}
                                    </td>
                                  </>
                                )}
                                {actCell}
                              </tr>
                            );
                          })}
                          {__tot > 1 && (
                            <tr>
                              <td colSpan={canSeeRates ? 11 : 7}>
                                <Pager cur={__cur} tot={__tot} setPage={setCrPage} />
                              </td>
                            </tr>
                          )}
                        </>
                      );
                    })()}
                  </tbody>
                </Table>
              </div>
            </>
          );
        })()}
    </div>
    {(_permViewer?.isAdmin || _isRPHere) && (
      <TeamAccessPanel
        scope={kind}
        scopeId={cs.id}
        allCollaborators={co}
        title={t("caseTeam.access_rights", {
          fallback: "Access rights",
        })}
        locked={teamViewLocked}
        onToggleLock={() => setTeamViewLocked((v) => !v)}
      />
    )}
        </>
      )}
    </div>
  );
}
